import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { rd, RemoteData } from '@passionware/monads';
import {
  Pagination as PaginationType,
  paginationUtils
} from 'v5/api/_common/query/pagination';
import { cn } from 'v5/platform/dom/cn';
import { suppressFocusRingProps } from './_common/focusRing';
import { Button, buttonVariants } from './Button';
import { ButtonGroup } from './ButtonGroup';
import { Input } from './Input';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Skeleton } from './Skeleton';

export interface PaginationWithPageDropdownProps {
  totalCount: RemoteData<number>;
  pagination: PaginationType;
  onPageChange: (pagination: PaginationType) => void;
  className?: string;
  variant?: 'inline' | 'default';
}

interface PaginationNavigationProps {
  totalPages: number;
  pagination: PaginationType;
  onPageChange: (pagination: PaginationType) => void;
  className?: string;
  variant: 'inline' | 'default';
}

const sanitizePageInput = (value: string) => value.replace(/\D/g, '');

const paginationSkeleton = (className?: string) => (
  <ButtonGroup className={className}>
    <Skeleton className="h-8 w-8 rounded" />
    <Skeleton className="h-8 w-24 rounded" />
    <Skeleton className="h-8 w-8 rounded" />
  </ButtonGroup>
);

/** Mouse took focus without :focus-visible; keyboard should show the group ring. */
const promoteFocusVisible = (group: HTMLElement) => {
  if (group.matches(':focus-visible')) return;
  group.blur();
  group.focus({ preventScroll: true });
};

function paginationSegmentProps(
  segmentVariant: 'ghost' | 'secondary',
  options: {
    disabled?: boolean;
    layout?: 'icon' | 'normal';
    className?: string;
  } = {}
) {
  return {
    role: 'button' as const,
    tabIndex: -1,
    className: cn(
      buttonVariants({
        size: 'sm',
        variant: segmentVariant,
        layout: options.layout,
        disabled: options.disabled,
        className: options.className
      })
    ),
    ...suppressFocusRingProps(true)
  };
}

function PaginationNavigation({
  totalPages,
  pagination,
  onPageChange,
  className,
  variant
}: PaginationNavigationProps) {
  const { t } = useTranslation('ds');
  const currentPage1Based = pagination.page + 1;
  const hasPrevious = pagination.page > 0;
  const hasNext = pagination.page < totalPages - 1;
  const paginationGroupRef = React.useRef<HTMLDivElement>(null);
  const [openJumpPopover, setOpenJumpPopover] = React.useState(false);
  const [jumpPageInput, setJumpPageInput] = React.useState(
    String(currentPage1Based)
  );
  const segmentVariant = variant === 'inline' ? 'ghost' : 'secondary';

  React.useEffect(() => {
    setJumpPageInput(String(currentPage1Based));
  }, [currentPage1Based]);

  const handlePageChange = (page1Based: number) => {
    const newPage = page1Based - 1;
    if (newPage < 0 || newPage >= totalPages) return;

    onPageChange({
      ...pagination,
      page: newPage
    });
  };

  const handlePrevious = () => {
    onPageChange(paginationUtils.prevPage(pagination));
  };

  const handleNext = () => {
    onPageChange(paginationUtils.nextPage(pagination));
  };

  const handleJumpPopoverOpenChange = (open: boolean) => {
    if (open) {
      setJumpPageInput(String(currentPage1Based));
    }
    setOpenJumpPopover(open);
  };

  const parsedJumpPage = Number.parseInt(jumpPageInput, 10);
  const isJumpPageValid =
    !Number.isNaN(parsedJumpPage) &&
    parsedJumpPage >= 1 &&
    parsedJumpPage <= totalPages;

  const submitJumpToPage = (event: React.FormEvent) => {
    event.preventDefault();
    if (!isJumpPageValid) return;

    handlePageChange(parsedJumpPage);
    setOpenJumpPopover(false);
  };

  const onPaginationKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target instanceof HTMLInputElement) {
      return;
    }

    const group = event.currentTarget;
    const run = (action: () => void) => {
      promoteFocusVisible(group);
      event.preventDefault();
      action();
    };

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        if (!hasPrevious) return;
        run(handlePrevious);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        if (!hasNext) return;
        run(handleNext);
        break;
      case 'Home':
        if (pagination.page === 0) return;
        run(() => handlePageChange(1));
        break;
      case 'End':
        if (pagination.page >= totalPages - 1) return;
        run(() => handlePageChange(totalPages));
        break;
      case 'Enter':
      case ' ':
        if (openJumpPopover || event.target !== group) {
          return;
        }
        run(() => handleJumpPopoverOpenChange(true));
        break;
    }
  };

  return (
    <ButtonGroup
      ref={paginationGroupRef}
      tabIndex={0}
      role="navigation"
      aria-label={t('Pagination.navigation')}
      onMouseDownCapture={event => {
        if (event.target === event.currentTarget) return;
        event.preventDefault();
        event.currentTarget.focus({
          preventScroll: true,
          focusVisible: false
        } as FocusOptions);
      }}
      onKeyDown={onPaginationKeyDown}
      className={cn(
        buttonVariants({ variant: 'headless' }),
        // headless still inherits flex/gap/justify from CVA defaults — keep a tight group
        'inline-flex w-fit gap-0 justify-start rounded p-0 h-auto min-h-0',
        '*:focus-visible:ring-0',
        className
      )}
    >
      <div
        {...paginationSegmentProps(segmentVariant, {
          layout: 'icon',
          disabled: !hasPrevious
        })}
        aria-disabled={!hasPrevious || undefined}
        aria-label={t('Pagination.previousPage')}
        onClick={() => {
          if (hasPrevious) handlePrevious();
        }}
      >
        <ChevronLeft />
      </div>

      <Popover
        open={openJumpPopover}
        onOpenChange={handleJumpPopoverOpenChange}
      >
        <PopoverTrigger asChild>
          <div
            {...paginationSegmentProps(segmentVariant, {
              className: 'min-w-20 tabular-nums'
            })}
            aria-current="page"
            aria-haspopup="dialog"
            aria-expanded={openJumpPopover}
            aria-label={t('Pagination.page', {
              current: currentPage1Based,
              total: totalPages
            })}
          >
            {t('Pagination.pageFraction', {
              current: currentPage1Based.toLocaleString(),
              total: totalPages.toLocaleString()
            })}
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-max min-w-0 p-2"
          align="center"
          sideOffset={8}
          onCloseAutoFocus={event => {
            event.preventDefault();
            paginationGroupRef.current?.focus({ preventScroll: true });
          }}
        >
          <form
            className="flex w-fit items-center gap-2"
            onSubmit={submitJumpToPage}
          >
            <Input
              type="text"
              inputMode="numeric"
              size="sm"
              value={jumpPageInput}
              onChange={e =>
                setJumpPageInput(sanitizePageInput(e.currentTarget.value))
              }
              aria-label={t('Pagination.jumpInput')}
              placeholder={t('Pagination.jumpPlaceholder', {
                total: totalPages.toLocaleString()
              })}
              className="w-34"
            />
            <Button
              type="submit"
              size="sm"
              variant={segmentVariant}
              disabled={!isJumpPageValid}
            >
              {t('Pagination.jumpAction')}
            </Button>
          </form>
        </PopoverContent>
      </Popover>

      <div
        {...paginationSegmentProps(segmentVariant, {
          layout: 'icon',
          disabled: !hasNext
        })}
        aria-disabled={!hasNext || undefined}
        aria-label={t('Pagination.nextPage')}
        onClick={() => {
          if (hasNext) handleNext();
        }}
      >
        <ChevronRight />
      </div>
    </ButtonGroup>
  );
}

export function PaginationWithPageDropdown({
  totalCount,
  pagination,
  onPageChange,
  className,
  variant = 'default'
}: PaginationWithPageDropdownProps) {
  const totalCountWithPlaceholder = rd.useLastWithPlaceholder(totalCount);

  return rd
    .journey(totalCountWithPlaceholder)
    .wait(paginationSkeleton(className))
    .catch(() => null)
    .map(count => {
      const totalPages = paginationUtils.getTotalPages(pagination, count);

      if (count <= pagination.pageSize) {
        return null;
      }

      return (
        <PaginationNavigation
          totalPages={totalPages}
          pagination={pagination}
          onPageChange={onPageChange}
          className={className}
          variant={variant}
        />
      );
    });
}
